import {Suspense, useEffect, useState} from 'react'
import {Outlet} from 'react-router-dom'
import {I18nProvider} from '../_metronic/i18n/i18nProvider'
import {LayoutProvider, LayoutSplashScreen} from '../_metronic/layout/core'
import {MasterInit} from '../_metronic/layout/MasterInit'
import {AuthInit, useAuth} from './modules/auth'
import { useToast } from './pages/components/Toats'
import { AlertToastProvider } from './pages/components/AlertToasts'
import AlertSocketListener from './pages/components/AlertToasts/AlertSocketListener'
import VerificationCodeListener from './pages/components/AlertToasts/VerificationCodeListener'
import useSocket from './hooks/useSocket'
import { removeLoadingCredential } from './utils/LoadingCredentials'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { hexToRgb } from './utils/colors'
import useSize from './hooks/useSize'
import { NuklieProvider } from './pages/components/Nuklie'

declare global {
  interface Window {
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (
        id: string,
        userInfo: {
          email?: string;
          name?: string;
          company_name?: string;
          companyId?: string;
        }
      ) => void;
      reset?: () => void;
      toggle?: (action: 'open' | 'close') => void;
    };
  }
  interface Window {
    $sleek?: any[];
    SLEEK_PRODUCT_ID?: number;
    SLEEK_USER?: {
      token: string;
    };
  }

}

const App = () => {
  const {currentUser} = useAuth()
  const { toggleToast } = useToast();
  const { socket, on, off } = useSocket();
  const [queryClient] = useState(() => new QueryClient());
  const {width} = useSize();

  const handleEventsSocket = (data: any) => {
    if (data.success) {
      toggleToast(data.message, 'success');
    } else {
      toggleToast(data.message, 'error');
    }
    removeLoadingCredential(data.idCredential);
  }

  const handleEventsSocketAtualiztionDates = (data: any) => {
    if (data.success) {
      toggleToast(data.message, 'success');
    } else {
      toggleToast(data.message, 'error');
    }
  }

  const chatwoot = (): void => {
    (function (d: Document, t: string) {
      const BASE_URL = "https://chat.lekko.com.br";
      const g = d.createElement(t) as HTMLScriptElement;
      const s = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + "/packs/js/sdk.js";
      g.defer = true;
      g.async = true;
      s.parentNode?.insertBefore(g, s);
      g.onload = () => {
        window.chatwootSDK?.run({
          websiteToken: "4huLsTooi3d2KNJmf1oGYBSz",
          baseUrl: BASE_URL,
        });
      };
    })(document, "script");

    window.addEventListener("chatwoot:ready", () => {
      window.$chatwoot?.setUser(currentUser && currentUser.id ? currentUser.id: "123456", {
        email: currentUser && currentUser.email ? currentUser.email : "usuario@teste.com",
        name: currentUser && currentUser.name ? currentUser.name : "Teste",
        company_name: currentUser 
          && currentUser.companies 
          && currentUser.companies.length > 0 
          && currentUser.companies[0].corporateName 
          ? currentUser.companies[0].corporateName 
          : "" ,
        companyId: currentUser 
          && currentUser.companies 
          && currentUser.companies.length > 0 
          && currentUser.companies[0].id 
          ? currentUser.companies[0].id 
          : "",
      });
    });
  };

  const cleanupChatwoot = (): void => {
    if (window.$chatwoot?.reset) {
      window.$chatwoot.reset();
    }
    if (window.$chatwoot?.toggle) {
      window.$chatwoot.toggle('close');
    }

    // Remove scripts
    const scripts = document.querySelectorAll('script[src*="chat.lekko.com.br"], script[src*="chatwoot"]');
    scripts.forEach(script => script.remove());
    
    // Remove TODOS os elementos do Chatwoot (usam prefixo "woot" e "chatwoot")
    const chatwootElements = document.querySelectorAll(
      '[id*="chatwoot"], [class*="chatwoot"], [id*="cw-widget"], [class*="woot-widget"], [class*="woot--bubble"], iframe[src*="chat.lekko.com.br"]'
    );
    chatwootElements.forEach(element => element.remove());
    
    if (window.chatwootSDK) delete window.chatwootSDK;
    if (window.$chatwoot) delete window.$chatwoot;
    
    const styles = document.querySelectorAll('style[data-chatwoot]');
    styles.forEach(style => style.remove());
  };


  const sleekplan = (token: string) => {
    if (window.$sleek) return;
  
    window.$sleek = [];
    window.SLEEK_PRODUCT_ID = 242477879;
  
    window.SLEEK_USER = {
      token: token
    };
  
    (function () {
      const d = document;
      const s = d.createElement("script");
      s.src = "https://client.sleekplan.com/sdk/e.js";
      s.async = true;
      d.getElementsByTagName("head")[0].appendChild(s);
    })();
  };

  const cleanupSleekplan = (): void => {
    const scripts = document.querySelectorAll('script[src*="client.sleekplan.com"], script[src*="sleekplan"]');
    scripts.forEach(script => script.remove());
    
    // Remove TODOS os elementos do Sleekplan
    const sleekplanElements = document.querySelectorAll(
      '[data-sleekplan], [id*="sleekplan"], [class*="sleekplan"], #sleekplan-frame, iframe[src*="sleekplan"]'
    );
    sleekplanElements.forEach(element => element.remove());
    
    if (window.$sleek) delete window.$sleek;
    if (window.SLEEK_PRODUCT_ID) delete window.SLEEK_PRODUCT_ID;
    if (window.SLEEK_USER) delete window.SLEEK_USER;
  };


  const clarityScript = () => {
    (function (
      c: any,
      l: Document,
      a: string,
      r: string,
      i: string
    ) {
      c[a] =
        c[a] ||
        function (...args: any[]) {
          (c[a].q = c[a].q || []).push(args);
        };
  
      const t = l.createElement(r);
      (t as HTMLScriptElement).async = true;
      (t as HTMLScriptElement).src = "https://www.clarity.ms/tag/" + i;
  
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, "clarity", "script", "t562vchqi3");
  };

  useEffect(() => {
    if(currentUser && currentUser.companies && currentUser.companies.length > 0){
      if(currentUser.companies[0].color_primary){
        document.documentElement.style.setProperty('--bs-primary', currentUser.companies[0].color_primary);
        document.documentElement.style.setProperty('--bs-text-primary', currentUser.companies[0].color_primary);
        document.documentElement.style.setProperty('--bs-primary-active', currentUser.companies[0].color_primary);
        document.documentElement.style.setProperty('--bs-primary-rgb', hexToRgb(currentUser.companies[0].color_primary));
        document.documentElement.style.setProperty('--bs-component-active-bg', currentUser.companies[0].color_primary);
      }

      if(currentUser.companies[0].color_primary_variet){
        document.documentElement.style.setProperty('--bs-secondary', currentUser.companies[0].color_primary_variet);
        document.documentElement.style.setProperty('--bs-text-secondary', currentUser.companies[0].color_primary_variet);
      }
    }

    if(currentUser && currentUser.id && socket){
      on("message", handleEventsSocket);
      on("messageAtualizationDates", handleEventsSocketAtualiztionDates);
    }

    if(currentUser 
      && currentUser.role 
      && width > 1000
      && (currentUser.role.slug === "EMPRESA" 
        || currentUser.role.slug === "FILIAL"
    )){
      chatwoot();
      clarityScript();
      if (currentUser.sleekToken) {
        sleekplan(currentUser.sleekToken);
      }
    }

    return () => {
      if(socket){
        off("message", handleEventsSocket);
        off("messageAtualizationDates", handleEventsSocketAtualiztionDates);
      }
      if(!currentUser){
        cleanupChatwoot();
        cleanupSleekplan();
      }
    };
  }, [currentUser, socket, width])

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <LayoutProvider>
            <AuthInit>
              <AlertToastProvider>
                <NuklieProvider>
                  <AlertSocketListener />
                  <VerificationCodeListener />
                  <Outlet />
                  <MasterInit />
                </NuklieProvider>
              </AlertToastProvider>
            </AuthInit>
          </LayoutProvider>
        </I18nProvider>
      </QueryClientProvider>
    </Suspense>
  )
}

export {App}
